import { tierName, type BadgeDefinition, type TierName } from '@ppwr/convex/badges/catalog'
import { SILHOUETTE_PALETTE, TIER_PALETTES, type BadgePalette } from '../../../assets/badges/palettes'

export interface BadgeProgressRow {
  badgeId: string
  count: number
  streakCurrent?: number
  tierEarnedAt: number[]
  seenTier: number
}

export interface BadgeDisplayState {
  badge: BadgeDefinition
  count: number
  tier: number // 0 = unearned
  isEarned: boolean
  isComplete: boolean // at top tier
  tierName: TierName | null
  tierEarnedAt: number[]
  /** "count / next threshold" — shown in the grid for incomplete tiered badges. */
  progressLabel: string | null
  /**
   * Progress toward the next tier, using the same value shown in
   * `progressLabel`, capped to the 0–1 range. `null` for one-shots and
   * completed badges, which have no next tier to pursue.
   */
  progressToNextTier: number | null
  /** "count / first threshold" — shown in the modal while unearned (any counting kind, not one-shots). */
  modalProgressLabel: string | null
  /**
   * Description with {x} resolved to the CURRENT tier's threshold once the
   * badge is earned (it describes what you have), or the first tier's
   * threshold while unearned (what it takes to get it).
   */
  description: string
}

export function badgeDisplayState(badge: BadgeDefinition, row: BadgeProgressRow | undefined): BadgeDisplayState {
  const count = row?.count ?? 0
  const tierEarnedAt = row?.tierEarnedAt ?? []
  const tier = tierEarnedAt.length
  const isEarned = tier >= 1
  const isComplete = tier >= badge.tiers.length
  const isOneShot = badge.tiers.length === 1 && badge.tiers[0] === 1
  const nextThreshold = isComplete ? badge.tiers[badge.tiers.length - 1] : badge.tiers[tier]
  const describedThreshold = isEarned ? badge.tiers[tier - 1] : badge.tiers[0]
  // kind==='streak' badges (play_streak only) show the CURRENT run (the
  // actionable number) — the stored count is the best-ever run and only
  // ever drives tiers. kind==='max' streak-worded badges (daily10_streak,
  // cth_streak) have no streakCurrent, so their progress shows best-ever.
  const progressValue = badge.kind === 'streak' ? (row?.streakCurrent ?? 0) : count

  return {
    badge,
    count,
    tier,
    isEarned,
    isComplete,
    tierName: isEarned && !isOneShot ? tierName(badge, tier) : null,
    tierEarnedAt,
    progressLabel:
      !isOneShot && !isComplete
        ? `${progressValue.toLocaleString()} / ${nextThreshold.toLocaleString()} to ${tierName(badge, tier + 1)}`
        : null,
    progressToNextTier:
      !isOneShot && !isComplete ? Math.min(1, Math.max(0, progressValue / nextThreshold)) : null,
    modalProgressLabel:
      !isEarned && !isOneShot ? `${progressValue.toLocaleString()} / ${nextThreshold.toLocaleString()}` : null,
    description:
      describedThreshold === 1 && badge.descriptionSingular
        ? badge.descriptionSingular
        : badge.description.replace('{x}', describedThreshold.toLocaleString())
  }
}

/**
 * Palette for both the grid and the detail drawer: silhouette until first
 * earn, then the tier palette. (The drawer deliberately mirrors the grid —
 * there is no separate unearned preview palette.)
 */
export function gridPalette(state: BadgeDisplayState): BadgePalette {
  if (!state.isEarned) return SILHOUETTE_PALETTE
  return TIER_PALETTES[tierName(state.badge, state.tier)]
}
